"use client";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/legacy/image";
import { Link } from "components/shared/Link";
import JsxParser from "react-jsx-parser";
import { ArticleNavigation } from "components/shared/ArticleNavigation";
import cn from "classnames";
import s from "./EssayTypeArticle.module.scss";
import { Button } from "components/UI/buttons/Button";
import { adjustArticleContent } from "utils/helpers";
import { parse } from "node-html-parser";
import { CTABlock } from "@/components/shared/CTABlock";
import { renderToStaticMarkup } from "react-dom/server";
import { SpriteIcon } from "@/components/UI/SpriteIcon";
import IconArrowDown from "@/public/img/icons/arrow-down-alt.svg";
import { debounce } from "lodash";

export type EssayTypeArticleProps = {
  contentsTable?: boolean;
  className?: string;
  type?: "default" | "blog";
  ctaBlock?: boolean;
  collapsible?: boolean;
  article: {
    content: string;
    navigation?: {
      entity: string;
      prevLink?: {
        href: string;
        heading: string;
      };
      nextLink?: {
        href: string;
        heading: string;
      };
    };
  };
};
export const EssayTypeArticle = ({
  article,
  type = "default",
  className,
  ctaBlock,
  collapsible,
}: EssayTypeArticleProps) => {
  const container = useRef<HTMLDivElement>(null);

  const [isFullHeight, setFullHeight] = useState(false);

  const isFullHeightRef = useRef(isFullHeight);
  isFullHeightRef.current = isFullHeight;

  const articleContent: string = useMemo(() => {
    if (!ctaBlock) return article.content;
    const articleDom = parse(article.content);

    const body = articleDom.getElementsByTagName("body")[0] || articleDom;

    if (body) {
      const headings = Array.from(body.querySelectorAll("h2"));

      headings.forEach((el, index) => {
        if (index === 1 || index === 5) {
          el.insertAdjacentHTML(
            "beforebegin",
            renderToStaticMarkup(
              <CTABlock
                heading={
                  <>
                    It’s time <span>to jumpstart</span> your paper!{" "}
                  </>
                }
                subheading="Delegate your assignment to our experts and they will do the rest."
                btnText="Get custom essay"
                className={s.cta}
              />
            )
          );
        }
      });
    }

    return articleDom.toString();
  }, [article.content, ctaBlock]);

  // adjust content
  useEffect(() => {
    if (!container.current) return;

    adjustArticleContent(container.current);
  }, [container]);

  const lastVisibleNodeIndex = useMemo(() => {
    if (!collapsible) return 0;

    const root = parse(articleContent);

    const body = root.getElementsByTagName("body")[0] || root;

    const firstHeading = body.querySelector("h2:first-child");

    if (firstHeading) {
      // if heading exists then collapse 2 paragraphs after heading
      let lastIndex = 0;
      let currentNode = firstHeading.nextElementSibling;
      let newHeadingEncountered = false;
      let elemsCount = 1;

      while (currentNode) {
        elemsCount += 1;
        if (elemsCount > 3) break;
        if (newHeadingEncountered) continue;
        if (currentNode.tagName.startsWith("H")) {
          newHeadingEncountered = true;
          currentNode = currentNode.nextElementSibling;
          continue;
        }

        lastIndex += 1;
        currentNode = currentNode.nextElementSibling;
      }
      // nothing to collapse
      if (elemsCount <= 3) return 0;
      return lastIndex;
    } else {
      // if no heading then show only 2 first paragraphs
      const children = Array.from(body.childNodes).filter(({ nodeType }) => nodeType === 1);

      if (children.length > 2) return 1;

      return 0;
    }
  }, [collapsible, articleContent]);

  const calculateHeight = useCallback(
    (container: HTMLDivElement | null) => {
      const parent = container?.querySelector(".jsx-parser");
      if (!parent) return;
      const targetElement = parent.children[lastVisibleNodeIndex];
      if (!targetElement) return;

      const height =
        Array.from(parent.children).reduce(
          (prev, next, currentIndex) =>
            currentIndex > lastVisibleNodeIndex ? prev : prev + next.clientHeight + 10,
          0
        ) - 10;

      container?.setAttribute("style", `height: ${height}px`);
    },
    [lastVisibleNodeIndex]
  );

  useEffect(() => {
    if (!lastVisibleNodeIndex) return;
    const watchHeight = debounce(() => {
      if (isFullHeightRef.current) return;
      calculateHeight(container.current);
    }, 20);

    watchHeight();
    window.addEventListener("resize", watchHeight);

    return () => {
      window.removeEventListener("resize", watchHeight);
    };
  }, [calculateHeight, lastVisibleNodeIndex]);

  return (
    <div
      className={cn(
        s.essayArticle,
        {
          [s.essayArticleNoContentsTable]: true,
          [s.essayArticleBlog]: type === "blog",
          [s.collapsed]: !!lastVisibleNodeIndex && !isFullHeight,
          [s.fullHeight]: !!lastVisibleNodeIndex && isFullHeight,
        },
        className
      )}
    >
      <article ref={container} className={s.articleWrap}>
        {/* @ts-ignore */}
        <JsxParser
          components={{
            Button: Button as React.ComponentType<any>,
            Image: Image as React.ComponentType,
            Link: Link as React.ComponentType,
            a: Link as React.ComponentType,
          }}
          autoCloseVoidElements
          jsx={articleContent}
          renderError={(err) => <code style={{ background: "#ff00001f" }}>{JSON.stringify(err)}</code>}
          className={s.article}
        />
      </article>
      {!!lastVisibleNodeIndex && (
        <div className={s.buttonWrap}>
          <Button
            tag="button"
            type="secondary"
            className={s.moreBtn}
            onClick={() => {
              setFullHeight(!isFullHeight);
              if (!isFullHeight) {
                container.current?.setAttribute("style", `height: auto`);
              } else {
                calculateHeight(container.current);
              }
            }}
          >
            <SpriteIcon icon={IconArrowDown} />
            {isFullHeight ? "Show less" : "Show more"}
          </Button>
        </div>
      )}
      {article.navigation && <ArticleNavigation {...article.navigation} />}
    </div>
  );
};
